import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { UserPlus, Trash2, Edit2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function SubAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permsInfo, setPermsInfo] = useState({ all_permissions: [], role_presets: {} });
  const [showCreate, setShowCreate] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', password: '', phone: '', admin_role: 'read_only_admin', admin_permissions: [] });
  const [saving, setSaving] = useState(false);

  const fetchAdmins = () => {
    api.get('/admin/sub-admins').then(r => { setAdmins(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  const fetchPerms = () => {
    api.get('/admin/permissions').then(r => setPermsInfo(r.data)).catch(() => {});
  };
  useEffect(() => { fetchAdmins(); fetchPerms(); }, []);

  const rolePresetNames = {
    super_admin: 'Super Admin', verification_admin: 'Verification Admin',
    finance_admin: 'Finance Admin', support_admin: 'Support Admin',
    developer_admin: 'Developer Admin', read_only_admin: 'Read Only Admin', custom: 'Custom',
  };

  const handleRoleChange = (role) => {
    const presetPerms = permsInfo.role_presets[role] || [];
    setForm(p => ({ ...p, admin_role: role, admin_permissions: [...presetPerms] }));
  };

  const togglePerm = (perm) => {
    setForm(p => {
      const current = p.admin_permissions || [];
      const updated = current.includes(perm) ? current.filter(x => x !== perm) : [...current, perm];
      return { ...p, admin_permissions: updated, admin_role: 'custom' };
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/admin/sub-admins', form);
      toast.success('Sub-admin created!');
      setShowCreate(false);
      setForm({ email: '', name: '', password: '', phone: '', admin_role: 'read_only_admin', admin_permissions: [] });
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { admin_role: form.admin_role, admin_permissions: form.admin_permissions, name: form.name };
      if (form.password) payload.password = form.password;
      await api.patch(`/admin/sub-admins/${editAdmin._id}`, payload);
      toast.success('Sub-admin updated!');
      setEditAdmin(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sub-admin?')) return;
    try {
      await api.delete(`/admin/sub-admins/${id}`);
      toast.success('Sub-admin deleted');
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const openEdit = (admin) => {
    setEditAdmin(admin);
    setForm({
      email: admin.email, name: admin.name || '', password: '', phone: admin.phone || '',
      admin_role: admin.admin_role || 'read_only_admin',
      admin_permissions: admin.admin_permissions || [],
    });
  };

  const PermissionsGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 rounded-lg border border-slate-200 bg-slate-50">
      {permsInfo.all_permissions.map(p => (
        <label key={p} className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
          <Checkbox checked={(form.admin_permissions || []).includes(p)} onCheckedChange={() => togglePerm(p)} />
          <span className="text-slate-700">{p.replace(/_/g, ' ')}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>Sub-Admin Management</h1>
          <p className="text-slate-500 text-sm mt-0.5">Create and manage admin users with granular permissions</p>
        </div>
        <Button onClick={() => { setShowCreate(true); setForm({ email: '', name: '', password: '', phone: '', admin_role: 'read_only_admin', admin_permissions: permsInfo.role_presets['read_only_admin'] || [] }); }} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="create-subadmin-btn">
          <UserPlus size={16} className="mr-1.5" /> Create Sub-Admin
        </Button>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Create Sub-Admin</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name</Label><Input data-testid="subadmin-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" required /></div>
              <div><Label>Email</Label><Input data-testid="subadmin-email" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1" required /></div>
              <div><Label>Password</Label><Input data-testid="subadmin-password" type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} className="mt-1" placeholder="Min 6 characters" required /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="mt-1" /></div>
            </div>
            <div>
              <Label>Admin Role</Label>
              <Select value={form.admin_role} onValueChange={handleRoleChange}>
                <SelectTrigger className="mt-1" data-testid="subadmin-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(rolePresetNames).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Permissions ({(form.admin_permissions || []).length} selected)</Label>
              <PermissionsGrid />
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="create-subadmin-submit">{saving ? 'Creating...' : 'Create Sub-Admin'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editAdmin} onOpenChange={v => { if (!v) setEditAdmin(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Edit: {editAdmin?.name}</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" /></div>
            <div><Label>New Password (leave blank to keep)</Label><Input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} className="mt-1" placeholder="Leave blank" /></div>
            <div>
              <Label>Admin Role</Label>
              <Select value={form.admin_role} onValueChange={handleRoleChange}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(rolePresetNames).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Permissions ({(form.admin_permissions || []).length} selected)</Label>
              <PermissionsGrid />
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="update-subadmin-submit">{saving ? 'Saving...' : 'Update Sub-Admin'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Table */}
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>
          ) : admins.length === 0 ? (
            <div className="py-12 text-center">
              <ShieldCheck size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No admin users yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Name</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Email</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Role</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Permissions</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Created</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map(a => (
                  <TableRow key={a._id} className="hover:bg-slate-50/50" data-testid={`subadmin-row-${a._id}`}>
                    <TableCell className="text-sm font-medium text-slate-900">{a.name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{a.email}</TableCell>
                    <TableCell><span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full capitalize">{(a.admin_role || 'super_admin').replace(/_/g, ' ')}</span></TableCell>
                    <TableCell className="text-xs text-slate-500">{(a.admin_permissions || []).length || 'All'} perms</TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`edit-admin-${a._id}`}><Edit2 size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(a._id)} className="text-red-500 hover:text-red-700" data-testid={`delete-admin-${a._id}`}><Trash2 size={14} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
